/**
 * Live-Vorschau, rechte Spalte (sticky ab lg, im Fluss darunter auf Mobile). Rendert AUS
 * `sichtbareFelder()` (apps/widgets/src/felder.ts, reine Funktion, kein React/DOM) — EINE Quelle
 * fuer Feldauswahl/-reihenfolge zwischen dem Editor-Formular-State und dem, was der Loader auf
 * der echten Website zeigen wuerde. `dom.ts` aus demselben Paket bleibt bewusst aussen vor
 * (Shadow-DOM-gekoppelt, siehe Bauplan).
 */
import { sichtbareFelder, type Feld, type FormularKonfig, type FreierFeldTyp } from "@maestro/widgets";
import { sichtbareSchluessel, type Bedingungen } from "@maestro/widgets/regeln";
import { useState, type CSSProperties } from "react";
import { aussehenVariablen, type Aussehen } from "@maestro/widgets/aussehen";
import { Card, Checkbox, Icon, Input } from "../../ui/components";
import { knopfFarben } from "./steps/AussehenStep";
import type { WidgetEditor } from "./useWidgetEditorForm";
import { EDITOR_NACHFRAGE_FELDER, bereichLabels, bereinigeBedingungen, folgefrageFelder } from "./ablauf-modell";

const KACHEL = "rounded-xl border border-outline-variant/40 px-2 py-2 text-center font-label-md text-[12px] text-on-surface-variant";

function feldVorschau(f: Feld) {
  const gemeinsam = "input pointer-events-none !bg-surface-container-lowest text-[13px] opacity-80";
  switch (f.typ) {
    case "textarea":
      return <textarea disabled rows={2} className={gemeinsam} placeholder={f.label} />;
    case "checkbox":
      return (
        <label className="flex items-center gap-2 text-[13px] text-on-surface-variant">
          <Checkbox disabled /> {f.label}
        </label>
      );
    case "select":
      // Rein dekorative Vorschau (nie bedienbar): zeigt die erste Option im Look des
      // Auswahlfelds statt eines nativen Auswahlfelds (Design E6).
      return (
        <div aria-hidden="true" className={`${gemeinsam} flex items-center justify-between`}>
          <span className="truncate">{f.optionen?.[0] ?? f.label}</span>
          <Icon name="expand_more" className="text-[16px]" />
        </div>
      );
    default:
      return <input disabled type={f.typ} className={gemeinsam} placeholder={f.label} />;
  }
}

/** Punkt 134: dieselben `--maestro-*`-Variablen wie der Loader; ohne Knopf-Text die lesbare Farbe zum Akzent. */
function aussehenStil(a: Aussehen): CSSProperties {
  return { ...aussehenVariablen(a), "--maestro-knopf-text": knopfFarben(a).text, fontFamily: "var(--maestro-font, inherit)" } as CSSProperties;
}

const KNOPF_STIL: CSSProperties = {
  background: "var(--maestro-accent, #1f2937)",
  color: "var(--maestro-knopf-text, #fff)",
  borderRadius: "var(--maestro-radius, 8px)",
};

/** Knopf auf heller oder dunkler Seite (Umschalter), damit man sieht, wie er auf der eigenen Website wirkt. */
function KnopfAufSeite({ text }: { text: string }) {
  const [dunkel, setDunkel] = useState(false);
  return (
    <div className="space-y-1.5">
      <div role="group" aria-label="Seitenhintergrund" className="flex gap-1.5">
        {[false, true].map((d) => (
          <button
            key={String(d)}
            type="button"
            aria-pressed={dunkel === d}
            onClick={() => setDunkel(d)}
            className={`rounded-full border px-2.5 py-0.5 font-label-md text-[11px] ${dunkel === d ? "border-primary bg-primary/10 font-semibold text-primary" : "border-outline-variant/40 text-on-surface-variant"}`}
          >
            {d ? "Dunkle Seite" : "Helle Seite"}
          </button>
        ))}
      </div>
      <div className="rounded-lg p-3" style={{ background: dunkel ? "#1c1917" : "#ffffff" }} data-testid="preview-seite">
        <div className="w-full px-4 py-2 text-center text-[13px] font-semibold" style={KNOPF_STIL} data-testid="preview-knopf">
          {text}
        </div>
      </div>
    </div>
  );
}

export default function PreviewPane({ editor }: { editor: WidgetEditor }) {
  const { widget, form } = editor;
  if (!widget) return null;
  const stil = aussehenStil(form.aussehen);
  const ohneAussehen = Object.keys(aussehenVariablen(form.aussehen)).length === 0 && (
    <p className="font-label-md text-[11px] text-on-surface-variant" data-testid="preview-auto-hinweis">
      Auf der Website übernimmt das Widget ohne eigene Einstellung die Farbe des ersten Knopfs deiner Seite.
    </p>
  );

  if (widget.kind !== "form") {
    return (
      <Card data-testid="widget-preview" className="space-y-2 p-4" style={stil}>
        <p className="font-body-sm text-body-sm font-semibold text-on-surface dark:text-inverse-on-surface">Vorschau</p>
        <p className="font-label-md text-label-md text-on-surface-variant">
          Bewertungs-Widgets zeigen die zuletzt abgerufenen Google-Bewertungen — hier keine Live-Vorschau, da der Betrieb dafür
          erst tatsächlich beim Gast auf der Website steht.
        </p>
        <KnopfAufSeite text="Alle Bewertungen ansehen" />
        {ohneAussehen}
      </Card>
    );
  }

  // Nur vollstaendige Zeilen (Schluessel + Beschriftung, bei "select" mindestens eine Option) —
  // dieselbe Grundregel wie `leseFormularKonfig` beim Einlesen fremden JSONs, hier direkt auf dem
  // bereits typisierten Formular-State angewendet (sichtbareFelder() selbst prueft das nicht).
  const freieFelder = form.freieFelder
    .filter((z) => z.key.trim() && z.label.trim() && (z.typ !== "select" || z.optionenText.trim().length > 0))
    .map((z) => ({
      key: z.key,
      typ: z.typ as FreierFeldTyp,
      label: z.label,
      pflicht: z.pflicht,
      ...(z.typ === "select" ? { optionen: z.optionenText.split("\n").map((o) => o.trim()).filter(Boolean) } : {}),
    }));
  const konfig: FormularKonfig = { standardfelder: form.standardfelder, freieFelder };
  const alleFelder = sichtbareFelder(konfig);

  // Folgefragen (Punkt 129): welche Felder sind bei den Vorschau-Antworten sichtbar? Dieselbe
  // Regel wie im Loader und beim Server (`@maestro/widgets/regeln`).
  const folgeFelder = folgefrageFelder(form);
  const bedingungen = bereinigeBedingungen(form.bedingungen, folgeFelder) as Bedingungen;
  const werte = editor.vorschauWerte;
  const sichtbar = sichtbareSchluessel(bedingungen, werte, [...alleFelder.map((f) => f.key), ...Object.keys(bedingungen)]);
  const felder = alleFelder.filter((f) => sichtbar.has(f.key));
  const vorschauZeilen = Object.entries(werte).map(([feld, wert]) => {
    const quelle = folgeFelder.find((f) => f.key === feld);
    return { feld: quelle?.label ?? feld, wert: quelle?.antworten?.find((a) => a.wert === wert)?.label ?? wert };
  });
  const a = form.ablauf;
  const bereiche = bereichLabels(a.gaesteText);
  const genauSichtbar = form.ablaufAn && "gaesteGenau" in bedingungen && sichtbar.has("gaesteGenau");
  const dankeFelder = form.ablaufAn
    ? a.dankeseite.filter((k) => sichtbareSchluessel(bedingungen, werte, [k]).has(k)).map((k) => EDITOR_NACHFRAGE_FELDER.find((f) => f.key === k)?.label ?? k)
    : [];

  return (
    <Card data-testid="widget-preview" className="space-y-3 p-4" style={stil}>
      <p className="font-label-md text-label-md text-on-surface-variant">Vorschau</p>
      {form.title.trim() && (
        <h3 className="font-headline-sm text-headline-sm text-on-background dark:text-inverse-on-surface">{form.title}</h3>
      )}
      {form.description.trim() && <p className="font-body-sm text-body-sm text-on-surface-variant">{form.description}</p>}
      {vorschauZeilen.length > 0 && (
        <div role="status" className="rounded-lg bg-primary/10 p-2 font-label-md text-label-md text-primary" data-testid="preview-folge-hinweis">
          So sieht es aus, wenn {vorschauZeilen.map((z) => `${z.feld} „${z.wert}“`).join(" und ")} gewählt ist.{" "}
          <button type="button" className="underline" onClick={() => editor.setVorschauWerte({})}>
            Zurücksetzen
          </button>
        </div>
      )}
      {form.ablaufAn && (
        <div className="space-y-3" data-testid="preview-ablauf">
          {a.blick.filter((z) => z.trim()).length > 0 && (
            <ul className="list-disc space-y-0.5 pl-4 font-label-md text-label-md text-on-surface-variant">
              {a.blick.filter((z) => z.trim()).map((z, i) => (
                <li key={i}>{z}</li>
              ))}
            </ul>
          )}
          <div>
            <span className="mb-1 block font-label-md text-label-md font-semibold text-on-surface-variant">Bildschirm 1: Gruppengröße</span>
            <div className="grid grid-cols-3 gap-1.5">
              {bereiche.map((b) => (
                <div
                  key={b.untergrenze}
                  className={`${KACHEL} ${werte.gaesteBereich === String(b.untergrenze) ? "font-semibold" : ""}`}
                  style={werte.gaesteBereich === String(b.untergrenze) ? { ...KNOPF_STIL, borderColor: "var(--maestro-accent, #1f2937)" } : undefined}
                  data-testid="preview-kachel"
                >
                  {b.text}
                </div>
              ))}
            </div>
            {genauSichtbar && (
              <div className="mt-2" data-testid="preview-genau">
                <span className="mb-1 block font-label-md text-label-md text-on-surface-variant">Genaue Personenzahl (optional)</span>
                <Input disabled type="number" aria-label="Genaue Personenzahl (optional)" className="pointer-events-none !bg-surface-container-lowest text-[13px] opacity-80" />
              </div>
            )}
          </div>
          <div>
            <span className="mb-1 block font-label-md text-label-md font-semibold text-on-surface-variant">Bildschirm 2: An welchem Tag möchten Sie feiern?</span>
            <div className={KACHEL}>Kalender{a.kalender?.monat ? `, öffnet im Monat ${a.kalender.monat}` : ""}</div>
            <div className={`${KACHEL} mt-1.5`}>Wir sind noch flexibel – zweiten Tag wählen</div>
          </div>
          <span className="block font-label-md text-label-md font-semibold text-on-surface-variant">Bildschirm 3: Name und E-Mail</span>
          {a.menues.filter((m) => m.label.trim()).length > 0 && (
            <p className="font-label-md text-label-md text-on-surface-variant">
              Menü-Wunsch: {a.menues.filter((m) => m.label.trim()).map((m) => m.label).join(", ")}
            </p>
          )}
          {dankeFelder.length > 0 && (
            <div data-testid="preview-danke">
              <span className="mb-1 block font-label-md text-label-md font-semibold text-on-surface-variant">Auf der Dankeseite</span>
              <ul className="list-disc space-y-0.5 pl-4 font-label-md text-label-md text-on-surface-variant">
                {dankeFelder.map((t) => (
                  <li key={t}>{t}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
      <div className="space-y-2.5">
        {felder.map((f) => (
          <div key={f.key}>
            {f.typ !== "checkbox" && (
              <span className="mb-1 block font-label-md text-label-md text-on-surface-variant">
                {f.label}
                {f.pflicht && " *"}
              </span>
            )}
            {feldVorschau(f)}
          </div>
        ))}
      </div>
      <KnopfAufSeite text={form.submitLabel.trim() || "Anfrage senden"} />
      {ohneAussehen}
      <p className="font-label-md text-[11px] text-on-surface-variant">
        {form.privacyUrl.trim()
          ? "Mit dem Absenden stimmen Sie unseren Datenschutzhinweisen zu."
          : "Die Angaben werden zur Bearbeitung der Anfrage verarbeitet."}
      </p>
    </Card>
  );
}
